import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { FormEvent } from 'react';

import { T } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { Explain } from '@flipster/react/design-utils/explain';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { Input } from '@flipster/ui';

import { InvitationCodePortfolioIdContext, invitationCodeModalBunja } from './state';

interface InvitationCodeModalProps {
  portfolioId: string;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  onSuccess: () => void;
}

export const InvitationCodeModal = ({ portfolioId, isOpen, onClose, onExit, onSuccess }: InvitationCodeModalProps) => {
  return (
    <InvitationCodePortfolioIdContext value={portfolioId}>
      <InvitationCodeModalImpl isOpen={isOpen} onClose={onClose} onExit={onExit} onSuccess={onSuccess} />
    </InvitationCodePortfolioIdContext>
  );
};

interface InvitationCodeModalImplProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  onSuccess: () => void;
}

const InvitationCodeModalImpl = ({ isOpen, onClose, onExit, onSuccess }: InvitationCodeModalImplProps) => {
  const t = useTranslations();
  const { invitationCodeAtom, errorMessageAtom, isVerifiedAtom, isDisabledAtom, verifyInvitationCode } =
    useBunja(invitationCodeModalBunja);

  const [invitationCode, setInvitationCode] = useAtom(invitationCodeAtom);
  const [errorMessage, setErrorMessage] = useAtom(errorMessageAtom);
  const isVerified = useAtomValue(isVerifiedAtom);
  const isDisabled = useAtomValue(isDisabledAtom);

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    if (isDisabled) return;

    if ((await verifyInvitationCode()).success) {
      onClose();
      onSuccess();
    }
  }

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title label={<T id='Copy.r260305.Private_copy_access' />} />
      </Modal.Header>
      <Modal.Content css={stack.raw({ gap: 32, py: 8 })}>
        <form onSubmit={handleSubmit} className={stack({ gap: 32 })}>
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
            <T id='Copy.r260305.Enter_the_invitation_code_to_start_copying_once_verified_you_can_set_up_copy_tra' />
          </p>
          <div className={stack({ gap: 8 })}>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
              <T id='Copy.r260305.Invitation_code' />
            </p>
            <div className={stack({ gap: 4 })}>
              <Input
                placeholder={t('Common.r250822.Enter_code')}
                value={invitationCode}
                onChange={(e) => {
                  setInvitationCode(e.currentTarget.value.toUpperCase());
                  setErrorMessage('');
                }}
                isError={Boolean(errorMessage)}
                disabled={isVerified}
                suffix={
                  isVerified ? (
                    <CheckCircleIcon
                      size={20}
                      color={token('colors.content.state.positive')}
                      className={css({ mr: 12, flexShrink: 0 })}
                    />
                  ) : invitationCode ? (
                    <button
                      type='button'
                      onClick={() => {
                        setInvitationCode('');
                        setErrorMessage('');
                      }}
                      className={css({ mr: 12, flexShrink: 0, color: 'content.default.level-2', cursor: 'pointer' })}
                    >
                      <XCircleIcon size={20} />
                    </button>
                  ) : null
                }
              />
              {errorMessage && <Explain.Error>{errorMessage}</Explain.Error>}
            </div>
          </div>
          <button type='submit' hidden />
        </form>
      </Modal.Content>
      {!isVerified && (
        <Modal.Footer>
          <Modal.Button
            withSpinner
            label={<T id='Common.r250401.Confirm' />}
            disabled={isDisabled}
            onClick={handleSubmit}
          />
        </Modal.Footer>
      )}
    </Modal.Root>
  );
};
